import React from "react";
import ReactDOM from "react-dom";
import App from "./App";
import * as serviceWorker from "./serviceWorker";
import "./index.css";
import "./theme-accent-questor.css";

if (typeof window !== "undefined") {
	// Patch do ResizeObserver para evitar erros de loop
	if (window.ResizeObserver) {
		const OriginalResizeObserver = window.ResizeObserver;
		window.ResizeObserver = class ResizeObserverPatched extends OriginalResizeObserver {
			constructor(callback) {
				super((entries, observer) => {
					try {
						window.requestAnimationFrame(() => {
							try {
								callback(entries, observer);
							} catch (error) {
								// Ignora erros do ResizeObserver
								if (
									error?.message?.includes("ResizeObserver loop") ||
									error?.message?.includes("ResizeObserver loop limit")
								) {
									return;
								}
								throw error;
							}
						});
					} catch (error) {
						// Ignora erros do ResizeObserver
						if (
							error?.message?.includes("ResizeObserver loop") ||
							error?.message?.includes("ResizeObserver loop limit")
						) {
							return;
						}
						throw error;
					}
				});
			}
		};
	}

	const RESIZE_OBSERVER_PATTERNS = [
		"ResizeObserver loop completed",
		"ResizeObserver loop limit exceeded",
		"ResizeObserver loop",
	];

	const isResizeObserverError = (message) => {
		if (!message) {
			return false;
		}
		const msg = typeof message === "string" ? message : String(message);
		return RESIZE_OBSERVER_PATTERNS.some((pattern) =>
			msg.includes(pattern),
		);
	};

	// Intercepta erros antes do React processar - CAPTURA FASE
	const handleError = (event) => {
		const message =
			event?.message ||
			event?.error?.message ||
			event?.error?.error?.message ||
			event?.reason?.message ||
			event?.target?.toString?.() ||
			String(event);
		if (isResizeObserverError(message)) {
			event.stopImmediatePropagation?.();
			event.preventDefault?.();
			event.stopPropagation?.();
			if ("cancelBubble" in event) {
				event.cancelBubble = true;
			}
			if ("returnValue" in event) {
				event.returnValue = false;
			}
			// Retorna true para indicar que o erro foi tratado
			return true;
		}
		return false;
	};

	// Adiciona listeners em múltiplas fases para garantir captura
	// CAPTURA (true) - intercepta antes de qualquer outro listener
	window.addEventListener("error", handleError, { capture: true, passive: false });
	// BUBBLING (false) - intercepta depois
	window.addEventListener("error", handleError, { capture: false, passive: false });

	// Intercepta window.onerror - PRIMEIRA LINHA DE DEFESA
	const originalOnError = window.onerror;
	window.onerror = function (message, source, lineno, colno, error) {
		const msg = message || error?.message || String(message || "");
		if (
			isResizeObserverError(msg) ||
			isResizeObserverError(error?.message) ||
			isResizeObserverError(error?.error?.message)
		) {
			// Retorna true para suprimir o erro
			return true;
		}
		if (typeof originalOnError === "function") {
			return originalOnError(message, source, lineno, colno, error);
		}
		return false;
	};

	// Intercepta unhandledrejection
	window.addEventListener("unhandledrejection", (event) => {
		const message =
			event?.reason?.message ||
			event?.reason?.toString?.() ||
			String(event?.reason || "");
		if (isResizeObserverError(message)) {
			event.stopImmediatePropagation?.();
			event.preventDefault?.();
			event.stopPropagation?.();
			if ("cancelBubble" in event) {
				event.cancelBubble = true;
			}
		}
	});

	// Ignora erros do overlay do CRA - intercepta ANTES que o overlay seja criado
	// Usa um observer para interceptar quando o overlay for criado
	const interceptOverlay = () => {
		if (window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__) {
			const overlay = window.__REACT_ERROR_OVERLAY_GLOBAL_HOOK__;
			
			// Intercepta todos os métodos possíveis do overlay
			const methodsToIntercept = [
				"onError",
				"onUnhandledError",
				"handleError",
				"reportError",
			];
			
			methodsToIntercept.forEach((methodName) => {
				if (typeof overlay[methodName] === "function") {
					const originalMethod = overlay[methodName];
					overlay[methodName] = (...args) => {
						// Verifica todos os argumentos possíveis
						for (const arg of args) {
							const message =
								arg?.message ||
								arg?.error?.message ||
								arg?.error?.error?.message ||
								arg?.toString?.() ||
								(arg && typeof arg.toString === "function"
									? arg.toString()
									: String(arg || ""));
							if (isResizeObserverError(message)) {
								return;
							}
						}
						return originalMethod.apply(overlay, args);
					};
				}
			});
		}
	};

	// Tenta interceptar imediatamente
	interceptOverlay();

	// Tenta interceptar depois que o overlay for criado (pode ser assíncrono)
	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", interceptOverlay);
	} else {
		setTimeout(interceptOverlay, 0);
		setTimeout(interceptOverlay, 100);
		setTimeout(interceptOverlay, 500);
	}

	// Observa mudanças no window para capturar quando o overlay for adicionado
	if (typeof Proxy !== "undefined") {
		const originalDefineProperty = Object.defineProperty;
		Object.defineProperty = function (obj, prop, descriptor) {
			if (
				obj === window &&
				prop === "__REACT_ERROR_OVERLAY_GLOBAL_HOOK__"
			) {
				const result = originalDefineProperty.call(
					this,
					obj,
					prop,
					descriptor,
				);
				setTimeout(interceptOverlay, 0);
				return result;
			}
			return originalDefineProperty.call(this, obj, prop, descriptor);
		};
	}

	// Suprime no console.error - INTERCEPTA ANTES DO OVERLAY
	const originalConsoleError = window.console.error;
	window.console.error = function (...args) {
		// Verifica todos os argumentos de forma mais abrangente
		const hasResizeObserverError = args.some((arg) => {
			if (!arg) return false;
			const msg =
				arg?.message ||
				arg?.error?.message ||
				arg?.error?.error?.message ||
				arg?.toString?.() ||
				(typeof arg === "string" ? arg : String(arg));
			return isResizeObserverError(msg);
		});
		if (hasResizeObserverError) {
			// Suprime completamente o erro
			return;
		}
		// Verifica também se algum argumento é um objeto Error com mensagem do ResizeObserver
		const hasResizeObserverInError = args.some((arg) => {
			if (arg instanceof Error) {
				return isResizeObserverError(arg.message) || isResizeObserverError(arg.stack);
			}
			return false;
		});
		if (hasResizeObserverInError) {
			return;
		}
		originalConsoleError.apply(window.console, args);
	};

	// Suprime no console.warn também (alguns browsers usam warn)
	const originalConsoleWarn = window.console.warn;
	window.console.warn = function (...args) {
		const hasResizeObserverError = args.some((arg) => {
			if (!arg) return false;
			const msg =
				arg?.message ||
				arg?.error?.message ||
				arg?.toString?.() ||
				(typeof arg === "string" ? arg : String(arg));
			return isResizeObserverError(msg);
		});
		if (hasResizeObserverError) {
			return;
		}
		originalConsoleWarn.apply(window.console, args);
	};

	// ÚLTIMA CAMADA: Intercepta erros que chegam ao React Error Overlay
	// Tenta interceptar o handleError do overlay diretamente quando ele é chamado
	const originalError = Error;
	window.Error = function (...args) {
		const error = originalError.apply(this, args);
		const message = error?.message || String(args[0] || "");
		if (isResizeObserverError(message)) {
			// Cria um erro "silencioso" que não será processado
			error.message = "";
			error.stack = "";
		}
		return error;
	};
	window.Error.prototype = originalError.prototype;
}

ReactDOM.render(<App />, document.getElementById("root"));

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
